/**
 * Psychology Theme — main JS entry point.
 *
 * Handles: fixed nav (scroll/hide), mobile menu toggle, dropdown menus.
 */
// Lock/unlock body scroll — shared by the mobile menu, video popup and contact-form popup.
function toggleBodyScroll(lock) {
  document.body.style.overflow = lock ? 'hidden' : '';
}

document.addEventListener('DOMContentLoaded', () => {
  const nav = document.querySelector('.nav');
  const toggle = document.querySelector('.nav__toggle');
  const menu = document.querySelector('.nav__menu');
  const dropdownItems = document.querySelectorAll('.nav__item.has-dropdown');

  if (!nav || !toggle || !menu) return;

  // ── Dropdown helpers ────────────────────────────────────────────────────────

  const closeAllDropdowns = () => {
    dropdownItems.forEach((item) => item.classList.remove('is-open'));
  };

  // ── Mobile menu ─────────────────────────────────────────────────────────────

  const closeMenu = () => {
    menu.classList.remove('is-open');
    toggle.classList.remove('is-open');
    nav.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    toggleBodyScroll(false);
    closeAllDropdowns();
  };

  const openMenu = () => {
    menu.classList.add('is-open');
    toggle.classList.add('is-open');
    nav.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    toggleBodyScroll(true);
  };

  toggle.addEventListener('click', (e) => {
    e.preventDefault();
    menu.classList.contains('is-open') ? closeMenu() : openMenu();
  });

  // Close menu on outside click
  document.addEventListener('click', (e) => {
    if (!nav.contains(e.target)) {
      if (window.innerWidth <= 991) closeMenu();
      else closeAllDropdowns();
    }
  });

  // ── Dropdown menus ──────────────────────────────────────────────────────────

  dropdownItems.forEach((item) => {
    const link = item.querySelector('.nav__link');
    const dropdown = item.querySelector('.nav__dropdown');
    if (!link || !dropdown) return;

    // Mobile: toggle on link click
    link.addEventListener('click', (e) => {
      if (window.innerWidth > 991) return;
      e.preventDefault();
      e.stopPropagation();
      const isOpen = item.classList.contains('is-open');
      closeAllDropdowns();
      if (!isOpen) item.classList.add('is-open');
    });

    // Desktop: hover
    item.addEventListener('mouseenter', () => {
      if (window.innerWidth > 991) item.classList.add('is-open');
    });
    item.addEventListener('mouseleave', () => {
      if (window.innerWidth > 991) item.classList.remove('is-open');
    });
  });

  // ── Resize handler ──────────────────────────────────────────────────────────

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (window.innerWidth > 991) closeMenu();
    }, 200);
  });

  // ── Scroll: hide/show nav + scrolled state ──────────────────────────────────

  let lastScrollY = window.scrollY;

  const onScroll = () => {
    const y = window.scrollY;
    nav.classList.toggle('is-scrolled', y > 40);

    if (!nav.classList.contains('is-open')) {
      if (y > lastScrollY && y > 100) {
        nav.classList.add('is-hidden');
      } else {
        nav.classList.remove('is-hidden');
      }
    }

    lastScrollY = y;
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
});

// ── Video player: inline embed + popup (about & process blocks) ──────────────

document.addEventListener('DOMContentLoaded', () => {
  let popup = null;

  function buildPopup() {
    if (popup) return popup;
    popup = document.createElement('div');
    popup.className = 'video-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute('aria-label', 'Video player');
    popup.innerHTML = `
      <div class="video-popup__backdrop"></div>
      <div class="video-popup__wrap">
        <button class="video-popup__close" aria-label="Close video">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
        <div class="video-popup__player"></div>
      </div>`;
    document.body.appendChild(popup);
    popup.querySelector('.video-popup__backdrop').addEventListener('click', closePopup);
    popup.querySelector('.video-popup__close').addEventListener('click', closePopup);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closePopup(); });
    return popup;
  }

  function embedVideo(container, url, styles) {
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s?]+)/);
    if (ytMatch) {
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
      iframe.allowFullscreen = true;
      if (styles) iframe.style.cssText = styles;
      container.innerHTML = '';
      container.appendChild(iframe);
    } else {
      const video = document.createElement('video');
      video.src = url;
      video.controls = true;
      video.autoplay = true;
      if (styles) video.style.cssText = styles;
      container.innerHTML = '';
      container.appendChild(video);
    }
  }

  function openPopup(url) {
    const p = buildPopup();
    embedVideo(p.querySelector('.video-popup__player'), url, null);
    p.classList.add('is-open');
    toggleBodyScroll(true);
    p.querySelector('.video-popup__close').focus();
  }

  function closePopup() {
    if (!popup) return;
    popup.classList.remove('is-open');
    toggleBodyScroll(false);
    popup.querySelector('.video-popup__player').innerHTML = '';
  }

  // Thumbnail + video link — inline or popup
  document.querySelectorAll([
    '.about__video-link[data-video-mode]',
    '.process__video-link[data-video-mode]',
    '.uvisa__video-link[data-video-mode]',
    '.video__video-link[data-video-mode]',
    '.word-from__video-link[data-video-mode]',
    '.story-card__play[data-video-mode]',
    '.story-detail__play[data-video-mode]',
  ].join(', ')).forEach((link) => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const mode = link.dataset.videoMode;
      const url  = link.href;

      if (mode === 'popup') {
        openPopup(url);
      } else {
        // inline — embed directly into the parent container
        const frame = link.parentElement;
        embedVideo(frame, url, 'position:absolute;inset:0;width:100%;height:100%;border:0;object-fit:cover;background:#000;');
      }
    });
  });

  // Direct video (no thumbnail) — always plays inline
  document.querySelectorAll('.about__video-direct, .process__video-direct, .uvisa__video-direct, .video__video-direct, .word-from__video-direct').forEach((video) => {
    const frame = video.parentElement;
    const play  = frame && frame.querySelector('.about__play, .process__play, .uvisa__play, .video__play, .word-from__play');
    if (!play) return;

    play.style.cursor = 'pointer';
    play.addEventListener('click', () => {
      video.controls = true;
      video.play();
      play.style.display = 'none';
    });
  });
});

// ── Scroll-snap slider nav (reviews, photo strip) ─────────────────────────────

function initSliderNav(wrapSelector, trackSelector, prevSelector, nextSelector) {
  document.querySelectorAll(wrapSelector).forEach((wrap) => {
    const track = wrap.querySelector(trackSelector);
    const prev  = wrap.querySelector(prevSelector);
    const next  = wrap.querySelector(nextSelector);

    if (!track || !prev || !next) return;

    const updateNav = () => {
      prev.disabled = track.scrollLeft <= 0;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    };

    prev.addEventListener('click', () => {
      track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
    });

    track.addEventListener('scroll', updateNav, { passive: true });
    updateNav();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initSliderNav('.reviews__slider-wrap', '.reviews__cards--slider', '.reviews__btn--prev', '.reviews__btn--next');
});

// ── Buttonless scroll tracks: mouse-wheel + drag (photo strip) ────────────────
// Lets desktop mouse users scroll a horizontal track that has no nav buttons:
// vertical wheel is remapped to horizontal scroll, and the track is drag-able.

function initDragScroll(trackSelector) {
  document.querySelectorAll(trackSelector).forEach((track) => {
    let isDown = false;
    let startX = 0;
    let startScroll = 0;

    track.addEventListener('wheel', (e) => {
      if (track.scrollWidth <= track.clientWidth) return;
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      e.preventDefault();
      track.scrollLeft += e.deltaY;
    }, { passive: false });

    track.addEventListener('pointerdown', (e) => {
      if (track.scrollWidth <= track.clientWidth) return;
      isDown = true;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      track.setPointerCapture(e.pointerId);
      track.classList.add('is-dragging');
    });

    track.addEventListener('pointermove', (e) => {
      if (!isDown) return;
      track.scrollLeft = startScroll - (e.clientX - startX);
    });

    const stopDrag = () => {
      isDown = false;
      track.classList.remove('is-dragging');
    };

    track.addEventListener('pointerup', stopDrag);
    track.addEventListener('pointercancel', stopDrag);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initDragScroll('.photo-strip__row');
});

// ── Contact form popup ───────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  const popup   = document.getElementById('cf-popup');
  if (!popup) return;

  const openPopup = () => {
    popup.removeAttribute('hidden');
    popup.classList.add('is-open');
    toggleBodyScroll(true);
    popup.querySelector('.cf-popup__close')?.focus();
  };

  const closePopup = () => {
    popup.classList.remove('is-open');
    popup.setAttribute('hidden', '');
    toggleBodyScroll(false);
  };

  // Trigger: data-popup="contact-form" OR href="#contact-form"
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-popup="contact-form"], a[href="#contact-form"]');
    if (trigger) {
      e.preventDefault();
      openPopup();
    }
  });

  popup.querySelector('.cf-popup__backdrop')?.addEventListener('click', closePopup);
  popup.querySelector('.cf-popup__close')?.addEventListener('click', closePopup);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && popup.classList.contains('is-open')) closePopup();
  });
});

// ── Phone input mask: formats US numbers as (XXX) XXX-XXXX while typing ──────

document.addEventListener('DOMContentLoaded', () => {
  const formatPhone = (digits) => {
    digits = digits.slice(0, 10);
    if (digits.length === 0) return '';
    if (digits.length < 4) return `(${digits}`;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  document.querySelectorAll('.phone-input').forEach((input) => {
    input.setAttribute('pattern', '\\(\\d{3}\\) \\d{3}-\\d{4}');
    input.setAttribute('maxlength', '14');
    input.setAttribute('inputmode', 'numeric');

    input.addEventListener('input', () => {
      const digits = input.value.replace(/\D/g, '');
      input.value = formatPhone(digits);
    });
  });
});

// ── CF7 forms: disable submit button while the request is in flight ──────────
// Prevents double submits; CF7 fires 'wpcf7submit' once the AJAX request
// resolves (success, invalid, spam or mail-failed all trigger it), so that's
// the single hook needed to re-enable the button.

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.wpcf7-form').forEach((form) => {
    const submitBtn = form.querySelector('.wpcf7-submit');
    if (!submitBtn) return;

    form.addEventListener('submit', () => {
      submitBtn.disabled = true;
    });

    form.addEventListener('wpcf7submit', () => {
      submitBtn.disabled = false;
    });
  });
});

// ── FAQ accordion (shared init for .faq and .attorneys-faq) ──────────────────

function initFaqAccordion(container, triggerSel, itemSel, openClass) {
  const triggers = container.querySelectorAll(triggerSel);

  const closeItem = (t) => {
    const it = t.closest(itemSel);
    const ans = document.getElementById(t.getAttribute('aria-controls'));
    it.classList.remove(openClass);
    t.setAttribute('aria-expanded', 'false');
    if (ans) ans.hidden = true;
  };

  triggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest(itemSel);
      const isOpen = item.classList.contains(openClass);

      triggers.forEach(closeItem);

      if (!isOpen) {
        const answer = document.getElementById(trigger.getAttribute('aria-controls'));
        item.classList.add(openClass);
        trigger.setAttribute('aria-expanded', 'true');
        if (answer) answer.hidden = false;
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.attorneys-faq').forEach((el) => {
    initFaqAccordion(el, '.atfaq-item__trigger', '.atfaq-item', 'atfaq-item--open');
  });
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.faq').forEach((el) => {
    initFaqAccordion(el, '.faq-item__trigger', '.faq-item', 'faq-item--open');
  });
});

// ── About: expandable body text (clamped to media height) ────────────────────

function initAboutExpandableBody(section) {
  const wrap = section.querySelector('[data-body-expandable]');
  const frame = section.querySelector('.about__media-frame');
  if (!wrap || !frame) return;

  const body = wrap.querySelector('.about__body');
  const toggle = wrap.querySelector('[data-body-toggle]');
  if (!body || !toggle) return;

  const collapse = () => {
    const limit = frame.offsetHeight;
    body.style.maxHeight = '';
    const isOverflowing = body.scrollHeight > limit + 1;

    toggle.hidden = !isOverflowing;

    if (isOverflowing) {
      body.style.maxHeight = limit + 'px';
      toggle.setAttribute('aria-expanded', 'false');
    }
  };

  toggle.addEventListener('click', () => {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    toggle.setAttribute('aria-expanded', String(!expanded));
    body.style.maxHeight = expanded ? frame.offsetHeight + 'px' : body.scrollHeight + 'px';
  });

  collapse();

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (toggle.getAttribute('aria-expanded') !== 'true') collapse();
    }, 200);
  });
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.about').forEach((section) => initAboutExpandableBody(section));
});

// ── Case Stories List: filters, "Load more" & mobile filter accordion ────────

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.stories-list').forEach((block) => {
    const grid      = block.querySelector('[data-stories-grid]');
    const countEl   = block.querySelector('[data-stories-count]');
    const loadMore  = block.querySelector('[data-load-more]');
    const filterGroups = block.querySelectorAll('[data-filter-group]');

    if (!grid) return;

    const items    = Array.from(grid.querySelectorAll('.stories-list__grid-item'));
    const perPage  = parseInt(grid.dataset.perPage, 10) || items.length;
    const active   = { case_type: 'all', format: null };
    let visibleCount = perPage;

    const matches = (item) => {
      const caseTypes = item.dataset.caseType ? item.dataset.caseType.split(' ') : [];
      const caseOk = active.case_type === 'all' || caseTypes.includes(active.case_type);
      const formatOk = !active.format || item.dataset.format === active.format;
      return caseOk && formatOk;
    };

    const render = () => {
      const filtered = items.filter(matches);

      items.forEach((item) => { item.hidden = true; });
      filtered.slice(0, visibleCount).forEach((item) => { item.hidden = false; });

      if (countEl) {
        const shown = Math.min(visibleCount, filtered.length);
        countEl.textContent = `Showing ${shown} of ${filtered.length} case stories`;
      }

      if (loadMore) loadMore.hidden = visibleCount >= filtered.length;
    };

    // ── Filter link clicks ──────────────────────────────────────────────────
    filterGroups.forEach((group) => {
      const groupName = group.dataset.filterGroup;

      group.querySelectorAll('[data-filter-value]').forEach((link) => {
        link.addEventListener('click', () => {
          const value = link.dataset.filterValue;
          const isCaseType = groupName === 'case_type';

          if (!isCaseType && active.format === value) {
            // Clicking the active format again clears it
            active.format = null;
          } else {
            active[groupName] = value;
          }

          group.querySelectorAll('[data-filter-value]').forEach((l) => {
            l.classList.toggle('is-active', isCaseType ? l.dataset.filterValue === active.case_type : l.dataset.filterValue === active.format);
          });

          visibleCount = perPage;
          render();
        });
      });
    });

    // ── Load more ────────────────────────────────────────────────────────────
    if (loadMore) {
      loadMore.addEventListener('click', () => {
        visibleCount += perPage;
        render();
      });
    }

    // ── Mobile filter accordion ─────────────────────────────────────────────
    filterGroups.forEach((group) => {
      const toggle = group.querySelector('[data-filter-toggle]');
      if (!toggle) return;

      toggle.addEventListener('click', () => {
        const isOpen = group.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', String(isOpen));
      });
    });

    render();
  });
});

// ── Blog List: category filter, search & "Load more" ──────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.blog-list').forEach((block) => {
    const grid       = block.querySelector('[data-blog-grid]');
    const emptyEl    = block.querySelector('[data-blog-empty]');
    const searchInput = block.querySelector('[data-blog-search]');
    const filters    = block.querySelectorAll('[data-filter-value]');
    const loadMore   = block.querySelector('[data-load-more]');

    if (!grid) return;

    const items   = Array.from(grid.querySelectorAll('.blog-list__grid-item'));
    const perPage = parseInt(grid.dataset.perPage, 10) || items.length;
    let activeCategory = 'all';
    let searchTerm = '';
    let visibleCount = perPage;

    const matches = (item) => {
      const categories = item.dataset.category ? item.dataset.category.split(' ') : [];
      const categoryOk = activeCategory === 'all' || categories.includes(activeCategory);
      const title = item.querySelector('.blog-list-card__title')?.textContent.toLowerCase() ?? '';
      const searchOk = !searchTerm || title.includes(searchTerm);
      return categoryOk && searchOk;
    };

    const render = () => {
      const filtered = items.filter(matches);

      items.forEach((item) => { item.hidden = true; });
      filtered.slice(0, visibleCount).forEach((item) => { item.hidden = false; });

      if (emptyEl) emptyEl.hidden = filtered.length > 0;
      if (loadMore) loadMore.hidden = visibleCount >= filtered.length;
    };

    filters.forEach((filter) => {
      filter.addEventListener('click', () => {
        activeCategory = filter.dataset.filterValue;
        filters.forEach((f) => f.classList.toggle('is-active', f === filter));
        visibleCount = perPage;
        render();
      });
    });

    if (searchInput) {
      searchInput.addEventListener('input', () => {
        searchTerm = searchInput.value.trim().toLowerCase();
        visibleCount = perPage;
        render();
      });
    }

    if (loadMore) {
      loadMore.addEventListener('click', () => {
        visibleCount += perPage;
        render();
      });
    }

    render();
  });
});
